import {
    AddinsFlexPaneBody as LazyAddinsFlexPaneBody,
    AddinsFlexPaneHeader as LazyAddinsFlexPaneHeader,
} from 'owa-addins-view';
import { logUsage } from 'owa-analytics';
import getDrawerPaneId from 'owa-app-pane-group-utils/lib/getDrawerPaneId';
import { DrawerPane, isDrawerPaneVisible, type DrawerPaneResizeConfig } from 'owa-drawer';
import { observer } from 'owa-mobx-react';
import getModuleContextMailboxInfo from 'owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import { addinsHeader, addinsBody, addinsTitle } from '../AppPaneGroup.scss';
import { useState } from 'owa-react';
import { useCallback, useContext, useEffect, type FunctionComponent } from 'react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { isCapabilityEnabled } from 'owa-capabilities';
import { addinsResizablePaneCapability } from 'owa-capabilities-definitions/lib/addinsResizablePaneCapability';
import { isCopilotChatDrawerShown } from 'owa-copilot-chat-drawer-store/lib/selectors/isCopilotChatDrawerShown';
import { ResizeHandleScenario } from 'owa-resize-handle';
import { useWindowSize } from 'owa-react-hooks/lib/useWindowSize';
import isPopout from 'owa-popout-v2/lib/utils/isPopout';
import { getAddinMailboxInfoForWindow } from 'owa-addins-view/lib/utils/getAddinMailboxInfoForWindow';
import { addinsUseDrawerPaneInpopoutsCapability } from 'owa-capabilities-definitions/lib/addinsUseDrawerPaneInpopoutsCapability';

const ADDINS_DEFAULT_WIDTH = 320;
const ADDINS_EXPANDED_WIDTH_RATIO = 0.4;

function createAddinsHeader(
    mailboxInfo: any,
    isExpanded?: boolean,
    handleToggleExpand?: () => void,
    isCopilotShown?: boolean
) {
    return (
        <LazyAddinsFlexPaneHeader
            mailboxInfo={mailboxInfo}
            renderTaskPaneInPanel={true}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            isResizeDisabled={isCopilotShown}
        />
    );
}

const defaultExport: FunctionComponent<{}> = observer(function AddinsPane() {
    const paneId = getDrawerPaneId('addins');
    const targetWindow = useContext(ProjectionContext) ?? window;
    const isPopoutWindow = isPopout(targetWindow);
    const mailboxInfo =
        isCapabilityEnabled(addinsUseDrawerPaneInpopoutsCapability) && isPopoutWindow
            ? getAddinMailboxInfoForWindow(targetWindow) ?? getModuleContextMailboxInfo()
            : getModuleContextMailboxInfo();

    const shouldUseResizeHeader = isCapabilityEnabled(addinsResizablePaneCapability);

    const [paneWidth, setPaneWidth] = useState(ADDINS_DEFAULT_WIDTH);
    const isCopilotShown = isCopilotChatDrawerShown(targetWindow);

    const handleResize = useCallback(
        (newWidth: number) => {
            if (!shouldUseResizeHeader) {
                return;
            }
            logUsage('AddinPaneDrawerResize', mailboxInfo, {
                action: 'DragResize',
                previousWidth: paneWidth,
                newWidth,
                isPopoutWindow,
            });

            setPaneWidth(newWidth);
        },
        [paneWidth]
    );

    const { width: windowWidth } = useWindowSize('AddinsPane', targetWindow);
    const expandedWidth = useComputedValue(() => {
        if (!shouldUseResizeHeader) {
            return ADDINS_DEFAULT_WIDTH;
        }

        const value = Math.floor(windowWidth * ADDINS_EXPANDED_WIDTH_RATIO);
        return Math.max(ADDINS_DEFAULT_WIDTH, value);
    }, [windowWidth]);

    const handleToggleExpand = useCallback(() => {
        if (!shouldUseResizeHeader) {
            return;
        }

        const newWidth = paneWidth > ADDINS_DEFAULT_WIDTH ? ADDINS_DEFAULT_WIDTH : expandedWidth;
        logUsage('AddinPaneDrawerResize', mailboxInfo, {
            action: newWidth > ADDINS_DEFAULT_WIDTH ? 'Expand' : 'Collapse',
            previousWidth: paneWidth,
            newWidth,
            isPopoutWindow,
        });
        setPaneWidth(newWidth);
    }, [expandedWidth, paneWidth]);

    const isExpanded = shouldUseResizeHeader && !isCopilotShown && paneWidth > ADDINS_DEFAULT_WIDTH;

    useEffect(() => {
        if (shouldUseResizeHeader && isCopilotShown && isDrawerPaneVisible(paneId, targetWindow)) {
            logUsage('AddinPaneDrawerResize', mailboxInfo, {
                action: 'ResizeDisabled',
                previousWidth: paneWidth,
                newWidth: ADDINS_DEFAULT_WIDTH,
                isCopilotShown,
                isPopoutWindow,
            });
        }
    }, [isCopilotShown, paneWidth, paneId, targetWindow]);

    const resizeConfig: DrawerPaneResizeConfig | undefined = useComputedValue(
        () =>
            shouldUseResizeHeader
                ? {
                      scenario: ResizeHandleScenario.AddinsPane,
                      onResize: handleResize,
                  }
                : undefined,
        [handleResize]
    );

    return (
        <DrawerPane
            paneId={paneId}
            headerText={createAddinsHeader(
                mailboxInfo,
                shouldUseResizeHeader ? isExpanded : undefined,
                shouldUseResizeHeader ? handleToggleExpand : undefined,
                shouldUseResizeHeader ? isCopilotShown : undefined
            )}
            headerClassName={addinsHeader}
            titleClassName={addinsTitle}
            bodyClassName={addinsBody}
            showCloseButton={false}
            preserveContentOnDismiss={true}
            role={'complementary'}
            width={
                shouldUseResizeHeader
                    ? isCopilotShown
                        ? ADDINS_DEFAULT_WIDTH
                        : paneWidth
                    : undefined
            }
            resize={shouldUseResizeHeader && !isCopilotShown ? resizeConfig : undefined} // If Copilot is shown, we disable resizing to ensure there is enough room for both panes.
        >
            <LazyAddinsFlexPaneBody mailboxInfo={mailboxInfo} />
        </DrawerPane>
    );
}, 'AddinsPane');
export default defaultExport;
